import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search, Pause, Play, ArrowDownCircle, ArrowUpCircle, MoreVertical } from "lucide-react";
import { FaPrint } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const RecieptViewRE = ({ contractNo }) => {
    const COMPANY_CODE = localStorage.getItem("companyCode") || "-";
    const [receipts, setReceipts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [freesearch, setFreesearch] = useState("-");
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const receiptsPerPage = 50;
    const [selectedRV, setSelectedRV] = useState(null);
    const [holdStatusMap, setHoldStatusMap] = useState({});
    const [bounceStatusMap, setBounceStatusMap] = useState({});
    const [actionMenuOpenIndex, setActionMenuOpenIndex] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    // Close action menu on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (e.target.closest('[data-action-menu]')) return;
            setActionMenuOpenIndex(null);
        };
        if (actionMenuOpenIndex !== null) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [actionMenuOpenIndex]);

    // Reset when contractNo changes
    useEffect(() => {
        setCurrentPage(1);
        setFreesearch("-");
        setSearchTerm("");
        setHoldStatusMap({});
        setBounceStatusMap({});
    }, [contractNo]);

    // Fetch receipts when contractNo, page, or search changes
    useEffect(() => {
        fetchReceipts(currentPage, freesearch);
    }, [contractNo, currentPage, freesearch]);

    const fetchReceipts = async (page = 1, search = "-") => {
        setIsLoading(true);
        setError(null);
        try {
            const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
            const currentDepartment =
                localStorage.getItem("currentSelectedDepartment") || "MAIN";

            const apiUrl = `${apiPaths[0]?.API_CONFIG}/api/CRMTaskMainListFilter/${COMPANY_CODE}/CONTRACTRV/-/-/-/${contractNo}/${currentDepartment}/1900-01-01/1900-01-01/-/-/-`;

            const response = await axios.get(apiUrl);
            setReceipts(response.data || []);
            console.log(`Fetched receipts:`, response.data);
        } catch (err) {
            console.error("Error while fetching receipts:", err);
            setError("Failed to fetch receipts. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);

        // Default back to standard API flag '-' if string gets completely cleared out
        if (term.trim() === "") {
            setFreesearch("-");
        } else {
            setFreesearch(term);
        }
        setCurrentPage(1);
    };

    const totalPages = Math.max(1, Math.ceil(receipts.length / receiptsPerPage));

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const navigate = useNavigate();


    const onEdit = (rec) => {
        console.log("🚀 ~ onEdit ~ rec:", rec);

        const modifyVoucherData = {
            modifyVNO: rec.RV_No?.trim(),
            modifyVType: rec.type,
            modifyVDate: dayjs(rec.Date).format("YYYY-MM-DD"),
            amount: rec.amount,
            description: rec.Description,
        };

        navigate("/voucher", { state: { modifyVoucherData } });
    };

    const onPrint = (data) => {
        console.log("Print RV :", data);
        setSelectedRV(data);
        navigate(`/recieptPreviewRE`, { state: { rvPrintData: data } });
    };

    const handleHoldUnhold = (slNo, action) => {
        console.log("slNo",slNo)
        console.log("action:",action)
        if (!slNo) return;
        if (action === "hold") {
            setHoldStatusMap(prev => ({ ...prev, [slNo]: "HOLD" }));
            setBounceStatusMap(prev => {
                const next = { ...prev };
                delete next[slNo];
                return next;
            });
        } else {
            setHoldStatusMap(prev => {
                const next = { ...prev };
                delete next[slNo];
                return next;
            });
        }
    };

    const getEffectiveStatus = (rec) => {
        const slNo = rec.TR_slno;
        if (slNo !== undefined && holdStatusMap[slNo] !== undefined) return holdStatusMap[slNo];
        return (rec.status || "").trim();
    };

    const handleBounceRedepo = (slNo, action) => {
        if (!slNo) return;
        setBounceStatusMap(prev => ({ ...prev, [slNo]: action === "bounce" ? "BOUNCE" : "RE-DEPO" }));
        setHoldStatusMap(prev => {
            const next = { ...prev };
            delete next[slNo];
            return next;
        });
    };

    const getEffectiveBounceStatus = (rec) => {
        const slNo = rec.TR_slno;
        if (slNo !== undefined && bounceStatusMap[slNo] !== undefined) return bounceStatusMap[slNo];
        return "";
    };

    return (
        <div className="mt-6 ">
            <div className="flex justify-between items-center gap-6">
                <h2 className="text-xl text-gray-800 mb-4">Receipt View</h2>

                <div className="relative mb-4 w-[40%]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search by any field..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        aria-label="Search receipts"
                    />
                </div>

            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="flex items-center justify-center py-8 my-8 inset-0">
                    <div className="w-10 h-10 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto max-h-[70vh]">
                        <table className="min-w-full table-auto border-collapse">
                            <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">RV NO</th>
                                    <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">DATE</th>
                                    <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">DESCRIPTION</th>
                                    <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">AMOUNT</th>
                                    <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">CURRENCY</th>
                                    <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">CHEQUE NO</th>
                                    <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">TYPE</th>
                                    <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">STATUS</th>
                                    <th className="px-4 py-3 text-sm text-center font-medium text-gray-700 border-b">ACTION</th>
                                </tr>
                            </thead>
                            <tbody>
                                {receipts.length > 0 ? (
                                    receipts.map((rec, index) => (
                                        <tr key={rec.TR_slno || index} className={`transition-colors ${getEffectiveStatus(rec).toUpperCase() === "HOLD" ? "bg-amber-50 hover:bg-amber-100" : getEffectiveBounceStatus(rec) === "BOUNCE" ? "bg-red-50 hover:bg-red-100" : getEffectiveBounceStatus(rec) === "RE-DEPO" ? "bg-green-50 hover:bg-green-100" : "hover:bg-blue-50"}`}>
                                            <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.RV_No}>{rec.RV_No?.trim()}</td>
                                            <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.Date}>{dayjs(rec.Date).format("DD/MM/YYYY")}</td>
                                            <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.Description}>{rec.Description || "-"}</td>
                                            <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.amount}>{Number(rec.amount || 0).toFixed(2)}</td>
                                            <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.curno}>{rec.curno}</td>
                                            <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.chqno}>{rec.chqno || "-"}</td>
                                            <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.type}>{rec.type}</td>
                                            <td className="px-4 py-2 border-b text-sm text-gray-600">
                                                {getEffectiveStatus(rec).toUpperCase() === "HOLD" ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                                        HOLD
                                                    </span>
                                                ) : getEffectiveBounceStatus(rec) === "BOUNCE" ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                                        BOUNCE
                                                    </span>
                                                ) : getEffectiveBounceStatus(rec) === "RE-DEPO" ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                                                        RE-DEPO
                                                    </span>
                                                ) : (
                                                    <span>{getEffectiveStatus(rec) || "-"}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 border-b text-sm text-gray-600 relative">
                                                <div className="flex justify-center">
                                                    <button
                                                        data-action-menu
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (actionMenuOpenIndex === index) {
                                                                setActionMenuOpenIndex(null);
                                                            } else {
                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
                                                                setActionMenuOpenIndex(index);
                                                            }
                                                        }}
                                                        className="p-1.5 rounded hover:bg-gray-100 border border-gray-200 transition-colors"
                                                        title="Actions"
                                                    >
                                                        <MoreVertical size={16} className="text-gray-600" />
                                                    </button>

                                                    {actionMenuOpenIndex === index && (
                                                        <div
                                                            data-action-menu
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]"
                                                            style={{ top: menuPosition.top, left: menuPosition.left }}
                                                        >
                                                            {(() => {
                                                                const effectiveStatus = getEffectiveStatus(rec).toUpperCase();
                                                                const bounceStatus = getEffectiveBounceStatus(rec);
                                                                const isHold = effectiveStatus === "HOLD";
                                                                const isBounce = bounceStatus === "BOUNCE";
                                                                const isRedepo = bounceStatus === "RE-DEPO";

                                                                return (
                                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                const slNo = rec.TR_slno;
                                                                if (isHold) {
                                                                    handleHoldUnhold(slNo, "unhold");
                                                                } else {
                                                                    handleHoldUnhold(slNo, "hold");
                                                                }
                                                                setActionMenuOpenIndex(null);
                                                            }}
                                                            disabled={isBounce || isRedepo}
                                                            className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                                                                isBounce || isRedepo
                                                                    ? "text-gray-400 cursor-not-allowed"
                                                                    : isHold
                                                                        ? "text-amber-700 hover:bg-amber-50"
                                                                        : "text-orange-700 hover:bg-orange-50"
                                                            }`}
                                                        >
                                                            {isHold ? <Play size={14} /> : <Pause size={14} />}
                                                            {isHold ? "Unhold" : "Hold"}
                                                        </button>

                                                        <button
                                                            onClick={() => {
                                                                const slNo = rec.TR_slno;
                                                                handleBounceRedepo(slNo, "bounce");
                                                                setActionMenuOpenIndex(null);
                                                            }}
                                                            disabled={isHold}
                                                            className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                                                                isHold
                                                                    ? "text-gray-400 cursor-not-allowed"
                                                                    : isBounce
                                                                        ? "text-red-700 font-semibold bg-red-50"
                                                                        : "text-red-700 hover:bg-red-50"
                                                            }`}
                                                        >
                                                            <ArrowDownCircle size={14} />
                                                            Bounce
                                                        </button>

                                                        <button
                                                            onClick={() => {
                                                                const slNo = rec.TR_slno;
                                                                handleBounceRedepo(slNo, "redepot");
                                                                setActionMenuOpenIndex(null);
                                                            }}
                                                            disabled={isHold}
                                                            className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                                                                isHold
                                                                    ? "text-gray-400 cursor-not-allowed"
                                                                    : isRedepo
                                                                        ? "text-green-700 font-semibold bg-green-50"
                                                                        : "text-green-700 hover:bg-green-50"
                                                            }`}
                                                        >
                                                            <ArrowUpCircle size={14} />
                                                            Redepo
                                                        </button>

                                                                        <div className="border-t border-gray-100 my-1"></div>

                                                                        <button
                                                                            onClick={() => {
                                                                                onPrint(rec);
                                                                                setActionMenuOpenIndex(null);
                                                                            }}
                                                                            className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 text-slate-700 hover:bg-slate-50"
                                                                        >
                                                                            <FaPrint size={14} /> Print
                                                                        </button>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4 text-gray-500">No Receipts found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-between items-center mt-4">
                        <div className="text-sm text-gray-600">
                            Showing page {currentPage} ({receipts.length} receipts loaded)
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`px-3 py-1 rounded-lg ${currentPage === 1 ? "bg-gray-200 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                            >
                                Previous
                            </button>
                            <span className="px-3 py-1 text-sm text-gray-700">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={`px-3 py-1 rounded-lg ${currentPage === totalPages ? "bg-gray-200 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default RecieptViewRE;